/**
 * Centralized API Client with Refresh Token Support
 * Handles all HTTP requests with auth, token refresh, and error handling.
 */
import axios from 'axios';
import { toast } from 'sonner';
import { BACKEND_URL, USES_COOKIES, isPublicEndpoint, isAuthPage, API_ENDPOINTS } from '../config/api';
import { normaliseApiErrorDetail } from '../utils/errorNormaliser';

// ── Response-payload sanitiser ──────────────────────────────────────
const _sanitiseErrorPayload = (error) => {
  const data = error?.response?.data;
  if (!data || typeof data !== 'object' || data._shielded) return;

  if ('detail' in data && typeof data.detail !== 'string') {
    data.detail = normaliseApiErrorDetail(data.detail);
  }
  if ('message' in data && data.message != null && typeof data.message !== 'string') {
    data.message = normaliseApiErrorDetail(data.message);
  }
  if (
    'error' in data
    && data.error != null
    && typeof data.error !== 'string'
    && !Array.isArray(data.error)
    && typeof data.error === 'object'
  ) {
    data.error = normaliseApiErrorDetail(data.error);
  }
};

// ── Cloudflare/origin-error shield ──────────────────────────────────
const CF_COOLDOWN_AFTER_HIT_MS = 60_000;
// Safe GET/HEAD reads may cross a very short blue/green edge cutover. Keep the
// user out of that plumbing by retrying quietly for ~4.25s before surfacing a
// persistent fault. Mutating requests are NEVER replayed by this policy.
const TRANSIENT_GET_RETRY_DELAYS_MS = [350, 700, 1200, 2000];
const TRANSIENT_GET_RETRY_MAX_DELAY_MS = 2_500;

let _cfLastToastAt = 0;
let _cfCooldownUntil = 0;
let _activeMediaSessions = 0;

export const isInCfCooldown = () => (
  Date.now() < _cfCooldownUntil || _activeMediaSessions > 0
);

export const cfCooldownRemainingMs = () => Math.max(0, _cfCooldownUntil - Date.now());

export const setActiveMediaSession = (active) => {
  if (active) {
    _activeMediaSessions += 1;
    // eslint-disable-next-line no-console
    console.info('[ccuk-network] polling-paused-active-media', { sessions: _activeMediaSessions });
  } else {
    _activeMediaSessions = Math.max(0, _activeMediaSessions - 1);
    // eslint-disable-next-line no-console
    console.info('[ccuk-network] polling-resumed-active-media', { sessions: _activeMediaSessions });
  }
};

const _safeUrl = (raw) => {
  try {
    const s = String(raw || '');
    const noQuery = s.split('?')[0];
    const noFragment = noQuery.split('#')[0];

    if (/^https?:\/\//i.test(noFragment)) {
      try { return new URL(noFragment).pathname; } catch { /* ignore */ }
    }

    return noFragment;
  } catch {
    return '';
  }
};

const _isBackendApiRequest = (config) => {
  const requestUrl = `${config?.baseURL || ''}${config?.url || ''}`;
  return requestUrl.startsWith('/api/')
    || requestUrl.includes('/api/')
    || requestUrl.startsWith(BACKEND_URL);
};

const _looksLikeCloudflareOrOriginError = (error) => {
  const resp = error?.response;

  if (!resp) {
    const msg = (error?.message || '').toLowerCase();
    return msg.includes('network error')
      || msg.includes('failed to fetch')
      || msg.includes('econnaborted')
      || msg.includes('timeout');
  }

  const status = resp.status;
  const contentType = String(resp.headers?.['content-type'] || '').toLowerCase();
  const isHtml = contentType.includes('text/html');
  const body = typeof resp.data === 'string' ? resp.data.toLowerCase() : '';
  const looksLikeCf = body.includes('cloudflare')
    || body.includes('origin web server')
    || body.includes('could not parse')
    || body.includes('malformed http headers')
    || body.includes('empty response')
    || body.includes('just a moment');

  const isCfStatus = status >= 520 && status <= 530;
  const isGenericHtml5xx = status >= 500 && isHtml;
  const isWafChallenge = status === 429 && (isHtml || looksLikeCf);

  return isCfStatus || isGenericHtml5xx || isWafChallenge || looksLikeCf;
};

const _shieldCloudflareError = (error) => {
  const requestUrl = error?.config?.url || '';
  const method = (error?.config?.method || 'GET').toUpperCase();

  if (!String(requestUrl).includes('/api/')) return false;

  const status = error?.response?.status ?? 'network';
  const isPlain429 = status === 429 && !_looksLikeCloudflareOrOriginError(error);

  if (isPlain429) {
    _cfCooldownUntil = Date.now() + CF_COOLDOWN_AFTER_HIT_MS;
    // eslint-disable-next-line no-console
    console.info('[ccuk-network] cooldown-armed', {
      reason: 'plain-429',
      url_safe: _safeUrl(requestUrl),
      seconds: CF_COOLDOWN_AFTER_HIT_MS / 1000,
    });
    return false;
  }

  if (!_looksLikeCloudflareOrOriginError(error)) return false;

  const contentType = error?.response?.headers?.['content-type'] || '';
  const errorId = error?.response?.headers?.['x-error-id'] || null;
  const urlSafe = _safeUrl(requestUrl);

  // eslint-disable-next-line no-console
  console.warn('[ccuk-network] shield-triggered', {
    url_safe: urlSafe,
    method,
    status,
    contentType,
    error_id: errorId,
  });

  if (error.response) {
    error.response.data = {
      _shielded: true,
      error_id: errorId,
    };
  }

  const now = Date.now();
  const isSafeRead = method === 'GET' || method === 'HEAD';

  // The global connection toast is reserved for persistent safe-read failures.
  // Mutations are not retried and their caller can surface its own action-level
  // error without a background POST/heartbeat producing a scary global toast.
  if (isSafeRead && now - _cfLastToastAt > CF_COOLDOWN_AFTER_HIT_MS) {
    _cfLastToastAt = now;
    try {
      toast.error('Connection blip — please retry.', {
        id: 'ccuk-cf-shield',
        duration: 4000,
      });
    } catch (_) {
      /* sonner not mounted — safe to skip */
    }
  } else if (isSafeRead) {
    // eslint-disable-next-line no-console
    console.info('[ccuk-network] toast-deduped', {
      url_safe: urlSafe,
      since_last_toast_ms: now - _cfLastToastAt,
    });
  } else {
    // eslint-disable-next-line no-console
    console.info('[ccuk-network] global-toast-suppressed-mutation', {
      url_safe: urlSafe,
      method,
      status,
    });
  }

  _cfCooldownUntil = Date.now() + CF_COOLDOWN_AFTER_HIT_MS;
  // eslint-disable-next-line no-console
  console.info('[ccuk-network] cooldown-armed', {
    reason: 'shield',
    url_safe: urlSafe,
    seconds: CF_COOLDOWN_AFTER_HIT_MS / 1000,
  });

  return true;
};

const _sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const _retryAfterMs = (error) => {
  const raw = error?.response?.headers?.['retry-after'];
  if (raw == null || raw === '') return null;

  const asNumber = Number(raw);
  if (Number.isFinite(asNumber) && asNumber >= 0) {
    return Math.round(asNumber * 1000);
  }

  const asDate = Date.parse(raw);
  if (Number.isFinite(asDate)) {
    return Math.max(0, asDate - Date.now());
  }

  return null;
};

/**
 * Return the next retry delay for a safe GET/HEAD request, otherwise null.
 * Mutating requests are deliberately excluded to avoid duplicate actions.
 */
const _transientSafeRetryDelay = (error, config) => {
  if (!config || !_isBackendApiRequest(config)) return null;
  if (axios.isCancel?.(error) || error?.code === 'ERR_CANCELED') return null;

  const method = String(config.method || 'GET').toUpperCase();
  if (method !== 'GET' && method !== 'HEAD') return null;

  const status = error?.response?.status;
  const isNetworkFailure = !error?.response && _looksLikeCloudflareOrOriginError(error);
  const isRetryableStatus = status === 408
    || status === 425
    || status === 429
    || status === 500
    || status === 502
    || status === 503
    || status === 504
    || (status >= 520 && status <= 530);

  if (!isNetworkFailure && !isRetryableStatus) return null;

  const retryAttempt = Number(config._ccukTransientRetryAttempt || 0);
  if (!Number.isFinite(retryAttempt) || retryAttempt < 0) return null;

  // Rate limits are not deployment cutovers. Retry at most once and respect a
  // short Retry-After; never hammer an endpoint four times because of a 429.
  if (status === 429) {
    if (retryAttempt >= 1) return null;
    const retryAfter = _retryAfterMs(error);
    if (retryAfter != null) {
      return retryAfter <= TRANSIENT_GET_RETRY_MAX_DELAY_MS ? retryAfter : null;
    }
    return 800;
  }

  if (retryAttempt >= TRANSIENT_GET_RETRY_DELAYS_MS.length) return null;
  return TRANSIENT_GET_RETRY_DELAYS_MS[retryAttempt];
};

const _logTransientRetry = (config, delayMs, source, attempt) => {
  // eslint-disable-next-line no-console
  console.info('[ccuk-network] transient-get-retry', {
    source,
    url_safe: _safeUrl(config?.url),
    attempt,
    max_attempts: TRANSIENT_GET_RETRY_DELAYS_MS.length,
    delay_ms: delayMs,
  });
};

// ── Token management ────────────────────────────────────────────────
let inMemoryAccessToken = null;
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

export const tokenManager = {
  get: () => {
    if (inMemoryAccessToken) return inMemoryAccessToken;
    return localStorage.getItem('access_token');
  },

  set: (token) => {
    inMemoryAccessToken = token;
    if (token) {
      localStorage.setItem('access_token', token);
    } else {
      localStorage.removeItem('access_token');
    }
  },

  clear: () => {
    inMemoryAccessToken = null;
    localStorage.removeItem('access_token');
  },
};

// ── Axios clients ───────────────────────────────────────────────────
const apiClient = axios.create({
  baseURL: BACKEND_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: USES_COOKIES,
});

// Dedicated refresh client: deliberately has NO apiClient response
// interceptor. This prevents /auth/refresh failures recursively firing the
// normal 401 interceptor and incorrectly logging out a still-valid session.
const refreshClient = axios.create({
  baseURL: BACKEND_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

apiClient.interceptors.request.use(
  (config) => {
    const token = tokenManager.get();

    if (token && !USES_COOKIES && !config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Quiet bounded retries for safe reads absorb short edge cutovers before
    // the user sees the "Connection blip" toast.
    const retryDelay = _transientSafeRetryDelay(error, originalRequest);
    if (retryDelay != null) {
      const retryAttempt = Number(originalRequest._ccukTransientRetryAttempt || 0) + 1;
      originalRequest._ccukTransientRetryAttempt = retryAttempt;
      _logTransientRetry(originalRequest, retryDelay, 'apiClient', retryAttempt);
      await _sleep(retryDelay);
      return apiClient(originalRequest);
    }

    // Only shield/toast after the safe retry window is exhausted (or the
    // request was not eligible). This keeps genuine persistent faults visible.
    _shieldCloudflareError(error);
    _sanitiseErrorPayload(error);

    if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
      const requestUrl = originalRequest?.url || '';
      const isPublic = isPublicEndpoint(requestUrl);
      const onAuthPage = isAuthPage();
      const isRefreshEndpoint = requestUrl.includes('/auth/refresh');
      const isLoginEndpoint = requestUrl.includes('/auth/login');

      if (isPublic || onAuthPage || isRefreshEndpoint || isLoginEndpoint) {
        if (!isPublic && !onAuthPage) {
          tokenManager.clear();
          window.dispatchEvent(new CustomEvent('auth:unauthorized', {
            detail: {
              reason: isRefreshEndpoint ? 'refresh_expired' : 'unauthorized',
              refresh_status: error.response?.status || null,
            },
          }));
        }
        return Promise.reject(error);
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers = originalRequest.headers || {};
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return apiClient(originalRequest);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const refreshResp = await refreshClient.post(API_ENDPOINTS.AUTH_REFRESH, {});
        const newToken = refreshResp?.data?.access_token || refreshResp?.data?.token;

        if (!newToken) {
          const malformedRefreshError = new Error('Refresh response missing access_token');
          malformedRefreshError.code = 'CCUK_REFRESH_RESPONSE_INVALID';
          throw malformedRefreshError;
        }

        tokenManager.set(newToken);
        isRefreshing = false;
        processQueue(null, newToken);

        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = `Bearer ${newToken}`;

        // eslint-disable-next-line no-console
        console.info('[auth] access token refreshed', {
          triggering_request: _safeUrl(originalRequest?.url),
        });

        return apiClient(originalRequest);
      } catch (refreshError) {
        isRefreshing = false;
        processQueue(refreshError, null);

        const refreshStatus = refreshError?.response?.status || null;
        const terminalRefreshFailure = refreshStatus === 401 || refreshStatus === 403;

        if (terminalRefreshFailure) {
          tokenManager.clear();
          // eslint-disable-next-line no-console
          console.warn('[auth] refresh session invalid — clearing session', {
            at: new Date().toISOString(),
            triggering_request: _safeUrl(originalRequest?.url),
            refresh_status: refreshStatus,
          });
          window.dispatchEvent(new CustomEvent('auth:unauthorized', {
            detail: {
              reason: 'refresh_expired',
              triggering_request: _safeUrl(originalRequest?.url),
              refresh_status: refreshStatus,
            },
          }));
        } else {
          // IMPORTANT: network errors, rate limits and 5xx responses are NOT
          // proof that the user's refresh session is invalid. Keep both the
          // current user state and cached token; the next request can retry the
          // refresh once connectivity/origin health recovers.
          // eslint-disable-next-line no-console
          console.warn('[auth] refresh transient failure — keeping session', {
            at: new Date().toISOString(),
            triggering_request: _safeUrl(originalRequest?.url),
            refresh_status: refreshStatus,
            refresh_code: refreshError?.code || null,
          });
        }

        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export const apiRequest = {
  get: (url, config = {}) => apiClient.get(url, config),
  post: (url, data, config = {}) => apiClient.post(url, data, config),
  put: (url, data, config = {}) => apiClient.put(url, data, config),
  patch: (url, data, config = {}) => apiClient.patch(url, data, config),
  delete: (url, config = {}) => apiClient.delete(url, config),
};

// ── Compatibility bridge for legacy raw-axios callsites ────────────
let rawAxiosAuthBridgeInstalled = false;

export const installRawAxiosAuthBridge = () => {
  if (rawAxiosAuthBridgeInstalled) return;
  rawAxiosAuthBridgeInstalled = true;

  axios.interceptors.request.use(
    (config) => {
      const token = tokenManager.get();

      if (token && _isBackendApiRequest(config)) {
        config.headers = config.headers || {};
        if (!config.headers.Authorization) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      }

      return config;
    },
    (error) => Promise.reject(error)
  );

  axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;
      const retryDelay = _transientSafeRetryDelay(error, originalRequest);

      if (retryDelay != null) {
        const retryAttempt = Number(originalRequest._ccukTransientRetryAttempt || 0) + 1;
        originalRequest._ccukTransientRetryAttempt = retryAttempt;
        _logTransientRetry(originalRequest, retryDelay, 'raw-axios', retryAttempt);
        await _sleep(retryDelay);
        return axios(originalRequest);
      }

      _shieldCloudflareError(error);
      _sanitiseErrorPayload(error);
      return Promise.reject(error);
    }
  );
};

installRawAxiosAuthBridge();

export default apiClient;